import {useLogoutMutation} from '../../hooks/session/mutations/useLogoutMutation';
import {Alert, Checkbox, CircularProgress, FormControlLabel} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {ConsoleLogger} from 'aws-amplify/utils';
import {Crisp} from 'crisp-sdk-web';
import React, {SyntheticEvent, useState, useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {useOutletContext, Link, useLocation} from 'react-router-dom';

import {readFirstTouch} from '../../analytics/firstTouch';
import {useMixpanelInstance} from '../../analytics/useMixpanelInstance';
import {useCrisp} from '../../components/CrispProvider';
import {useSignUpMutation} from '../../hooks/auth/mutations/useSignUpMutation';
import {useDocumentTitle} from '../../useDocumentTitle';
import {HANDLES_API_FAILURE} from '../../utils/mutationMeta';
// import pattern from '../../assets/svg/pattern-lines.svg';

const logger = new ConsoleLogger('Signup');

type SignupState = {
  state: 'idle' | 'loading' | 'error' | 'emailsent';
  email: string;
  cguchecked?: boolean;
  marketingChecked: boolean;
  error?: {message: string; emailAlreadyExists?: boolean};
  invitationId?: string;
};

// Message brut renvoyé par l'API `signup` quand l'adresse a déjà un compte.
const EMAIL_ALREADY_EXISTS_MESSAGE = 'email already exist';

const isErrorMessage = (obj: unknown): obj is {message: string} => {
  const isObject = (obj: unknown): obj is Partial<Record<'message', unknown>> =>
    typeof obj === 'object' && obj !== null;
  return isObject(obj) && 'message' in obj && typeof obj.message === 'string';
};

export function Signup() {
  const location = useLocation();
  const mp = useMixpanelInstance();
  const {lang} = useOutletContext<{lang: 'de' | 'en' | 'es' | 'fr' | 'it' | 'pt'}>();
  const {crisp} = useCrisp();

  // Lecture du paramètre invitationId dans l'URL
  const searchParams = new URLSearchParams(location.search);
  const invitationIdFromUrl = searchParams.get('invitationId') || undefined;

  const [signupState, setSignupState] = useState<SignupState>({
    state: 'idle',
    cguchecked: false,
    marketingChecked: false,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    email: location.state?.email ?? '',
    invitationId: invitationIdFromUrl,
  });
  const {state, email, cguchecked, error, invitationId} = signupState;

  const {t} = useTranslation();
  // Déconnexion préventive avant l'inscription : son échec est ignoré (`onSignup`).
  const logout = useLogoutMutation(HANDLES_API_FAILURE);
  useDocumentTitle(t('signup.documenttitle'));

  const [resendCountdown, setResendCountdown] = useState<number | null>(60);
  const [resendAttempts, setResendAttempts] = useState(0);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (resendCountdown !== null && resendCountdown > 0) {
      countdownRef.current = setTimeout(() => {
        setResendCountdown((prev) => (prev !== null ? prev - 1 : null));
      }, 1000);
    } else if (resendCountdown === 0) {
      clearTimeout(countdownRef.current!);
      countdownRef.current = null;
    }
    return () => clearTimeout(countdownRef.current!);
  }, [resendCountdown]);

  const handleResendEmail = async () => {
    if (resendAttempts < 2) {
      await onSignup();
      setResendAttempts((prev) => prev + 1);
      setResendCountdown(60);
    }
  };

  // Échec affiché dans le formulaire (`signupState.error`).
  const signUp = useSignUpMutation(HANDLES_API_FAILURE);

  const onSignup = async () => {
    if (cguchecked === false) {
      return;
    }
    try {
      await logout.mutateAsync();
    } catch (e) {
      logger.error(e);
    }
    setSignupState((state) => ({...state, state: 'loading', error: undefined}));
    try {
      await signUp.mutateAsync({
        email,
        locale: lang,
        marketingChecked: signupState.marketingChecked,
        ...(invitationId ? {invitationId} : {}),
        acquisition: readFirstTouch(),
      });
      setSignupState((state) => ({...state, state: 'emailsent'}));
      // `signup-completed` part désormais du serveur, sur l'`_id` Mongo du compte créé : émis ici,
      // il restait sur l'appareil anonyme de cet onglet, que la première connexion (souvent
      // ailleurs, le mot de passe arrivant par e-mail) ne rattachait pas. Reste l'interaction.
      mp.track('signup-form-submitted', {source: 'web'});
    } catch (error) {
      if (isErrorMessage(error)) {
        const e = {message: error.message, emailAlreadyExists: error.message === EMAIL_ALREADY_EXISTS_MESSAGE};
        setSignupState((state) => ({...state, state: 'error', error: e}));
      } else {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        setSignupState((state) => ({...state, state: 'error', error: {message: `Unknown error: ${error}`}}));
      }
    }
  };

  const onEmailChange = (email: string) => {
    setSignupState((state) => ({...state, state: 'idle', email: email.toLowerCase(), error: undefined}));
  };

  return (
    <>
      <Box pt={3} px={3}>
        {state === 'emailsent' ? (
          <Typography sx={{fontWeight: 700, fontSize: '24px'}}>{t('signin.login.justsignup.default')}</Typography>
        ) : (
          <>
            <Typography sx={{fontWeight: 700, fontSize: '24px'}}>{t('signin.signup.title')}</Typography>
            <Typography color="secondary" sx={{fontWeight: 700, fontSize: '14px'}}>
              {t('signin.signup.subtitle')}
            </Typography>
          </>
        )}
      </Box>
      <Box p={3}>
        <Box component="form" role="form" onSubmit={(event: SyntheticEvent<HTMLFormElement>) => event.preventDefault()}>
          <Box mt={3}>
            {state === 'emailsent' ? (
              <>
                <Typography variant="body2" fontWeight="bold">
                  {t('signin.login.temppasswordsent', {email: email})}
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  {t('signin.login.temppasswordsentretrysafe')}
                </Typography>
                {resendAttempts < 2 ? (
                  <Button
                    sx={{mt: 2, px: 0}}
                    variant="text"
                    disabled={resendCountdown !== null && resendCountdown > 0}
                    onClick={handleResendEmail}
                  >
                    {resendCountdown !== null && resendCountdown > 0
                      ? t('signin.login.resendemail.countdown', {countdown: resendCountdown})
                      : t('signin.login.resendemail.default')}
                  </Button>
                ) : (
                  <Button
                    sx={{mt: 2, px: 0}}
                    variant="text"
                    onClick={() => {
                      crisp.openChat();
                      Crisp.message.showText(t('signin.login.justsignup.asksupportmsg'));
                    }}
                  >
                    {t('signin.login.justsignup.asksupport')}
                  </Button>
                )}
              </>
            ) : (
              <>
                <Box mb={2}>
                  <TextField
                    type="email"
                    autoComplete="username"
                    required
                    fullWidth
                    placeholder={t('form.email.placeholder')}
                    autoFocus
                    value={email}
                    onChange={(event) => {
                      onEmailChange(event.target.value);
                    }}
                    error={error !== undefined}
                  />
                  {error?.emailAlreadyExists === true ? (
                    <Alert severity="warning" sx={{mt: 2}}>
                      {t('signup.form.emailexists.message')}
                      <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
                        <Button variant="contained" size="small" component={Link} to="/signin" state={{email}}>
                          {t('signup.form.emailexists.signin')}
                        </Button>
                        <Button variant="outlined" size="small" component={Link} to="/reset" state={{email}}>
                          {t('signup.form.emailexists.reset')}
                        </Button>
                      </Box>
                    </Alert>
                  ) : (
                    error?.message !== undefined && (
                      <Typography variant="body2" fontWeight="bold" color="error">
                        {error.message}
                      </Typography>
                    )
                  )}
                </Box>
                <Box mb={2} px={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={cguchecked}
                        onChange={(e, c) => setSignupState((p) => ({...p, cguchecked: c}))}
                      />
                    }
                    label={
                      <Box
                        component="span"
                        // Le lien des CGU vient de la traduction : couleur du thème, pas le bleu du navigateur.
                        sx={{'& a': {color: 'primary.main'}}}
                        dangerouslySetInnerHTML={{
                          __html: t('signup.form.acceptcgs'),
                        }}
                      />
                    }
                  />
                </Box>
                <Box mb={2} px={2}>
                  <FormControlLabel
                    sx={{alignItems: 'flex-start'}}
                    control={
                      <Checkbox
                        checked={signupState.marketingChecked || false}
                        onChange={(e, c) => setSignupState((p) => ({...p, marketingChecked: c}))}
                      />
                    }
                    label={t('signup.form.acceptmarketing')}
                  />
                </Box>
                <Box mt={4} mb={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={(state === 'loading' || cguchecked) === false}
                    onClick={onSignup}
                  >
                    {state === 'loading' ? <CircularProgress /> : <span>{t('signin.form.signupbtn')}</span>}
                  </Button>
                </Box>
                <Typography>
                  {t('signin.signup.ctalogin')}{' '}
                  <Button variant="text" component={Link} to="/signin" state={{email}}>
                    {t('signin.login.signbtn')}
                  </Button>
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}
